import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, Heart, ShoppingCart, LogOut, LogIn, UserPlus, Truck, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable user/account dropdown. Shows login/register when logged out,
 * and a quick menu (Orders, Track Order, Wishlist, Cart, Logout) when logged in.
 *
 * Props:
 * - variant: 'dark' | 'light' — colour of the trigger icon (default: light)
 */
export default function UserMenu({ variant = 'light' }) {
  const { user, logout, authHeaders } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Pull count of in-flight product requests (status: new / sourcing) so we
  // can surface a small badge on the user-menu trigger. Only refetched when
  // the user changes — cheap enough to leave at mount time.
  useEffect(() => {
    if (!user) { setPendingRequests(0); return; }
    let cancelled = false;
    axios.get(`${API}/product-requests`, { headers: authHeaders() })
      .then(({ data }) => {
        if (cancelled) return;
        const reqs = data.requests || [];
        setPendingRequests(reqs.filter((r) => r.status === 'new' || r.status === 'sourcing').length);
      })
      .catch(() => { if (!cancelled) setPendingRequests(0); });
    return () => { cancelled = true; };
  }, [user, authHeaders]);

  const iconColor = variant === 'dark'
    ? 'text-white/70 hover:text-white'
    : 'text-gray-500 hover:text-[#1A1A1A]';

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="user-menu-trigger"
        aria-label="Account menu"
        className={`relative p-2 transition-colors ${iconColor}`}
      >
        <User size={20} />
        {user && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full ring-2 ring-white" />
        )}
        {user && pendingRequests > 0 && (
          <span data-testid="user-menu-request-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-[#FF2A2A] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 ring-2 ring-white">
            {pendingRequests > 9 ? '9+' : pendingRequests}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="user-menu-dropdown"
          className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
        >
          {user ? (
            <>
              <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-br from-[#FF2A2A]/5 to-transparent">
                <p className="text-sm font-bold text-[#1A1A1A] truncate">{user.name || 'Welcome back'}</p>
                <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
              </div>
              <nav className="py-2">
                <MenuLink to="/account?tab=orders" icon={Package} onClick={() => setOpen(false)} testId="menu-orders">
                  My Orders
                </MenuLink>
                <MenuLink to="/account?tab=orders#tracking" icon={Truck} onClick={() => setOpen(false)} testId="menu-track">
                  Track Order
                </MenuLink>
                <MenuLink to="/account?tab=wishlist" icon={Heart} onClick={() => setOpen(false)} testId="menu-wishlist">
                  Wishlist
                </MenuLink>
                <MenuLink to="/request-product" icon={Search} onClick={() => setOpen(false)} testId="menu-request-product">
                  Request a product
                  {pendingRequests > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                      {pendingRequests} pending
                    </span>
                  )}
                </MenuLink>
                <MenuLink to="/cart" icon={ShoppingCart} onClick={() => setOpen(false)} testId="menu-cart">
                  Shopping Cart
                  {itemCount > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-[#FF2A2A] text-white px-1.5 py-0.5 rounded">{itemCount}</span>
                  )}
                </MenuLink>
                <MenuLink to="/account?tab=profile" icon={User} onClick={() => setOpen(false)} testId="menu-profile">
                  My Profile
                </MenuLink>
              </nav>
              <div className="border-t border-gray-100 py-2">
                <button
                  onClick={handleLogout}
                  data-testid="menu-logout"
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#FF2A2A] transition-colors"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-bold text-[#1A1A1A]">Welcome to OGMini</p>
                <p className="text-[11px] text-gray-500">Sign in to track orders &amp; save favourites.</p>
              </div>
              <nav className="py-2">
                <MenuLink to="/login" icon={LogIn} onClick={() => setOpen(false)} testId="menu-login"
                  state={{ from: (typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/') }}>
                  Sign in
                </MenuLink>
                <MenuLink to="/register" icon={UserPlus} onClick={() => setOpen(false)} testId="menu-register"
                  state={{ from: (typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/') }}>
                  Create account
                </MenuLink>
                {/* Guest "Request a product" — customers who don't want
                    to make an account can still tell us what they're
                    looking for via WhatsApp number. When they sign up
                    later with the same number, /auth/register links
                    every prior request to their fresh account. */}
                <MenuLink to="/request-product" icon={Search} onClick={() => setOpen(false)} testId="menu-request-product-guest">
                  Request a product
                  <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                    No login
                  </span>
                </MenuLink>
                <MenuLink to="/cart" icon={ShoppingCart} onClick={() => setOpen(false)} testId="menu-cart">
                  Shopping Cart
                  {itemCount > 0 && (
                    <span className="ml-auto text-[10px] font-bold bg-[#FF2A2A] text-white px-1.5 py-0.5 rounded">{itemCount}</span>
                  )}
                </MenuLink>
              </nav>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, state, icon: Icon, onClick, testId, children }) {
  return (
    <Link
      to={to}
      state={state}
      onClick={onClick}
      data-testid={testId}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1A1A1A] transition-colors"
    >
      <Icon size={14} />
      {children}
    </Link>
  );
}
